import { useState } from "react";

const statusBadgeClass = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "completed" || normalized === "paid" || normalized === "active") {
    return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
  }
  if (normalized === "partially paid" || normalized === "pending") {
    return "bg-amber-50 text-amber-700 ring-amber-600/20";
  }
  if (normalized === "blocked" || normalized === "failed" || normalized === "unpaid") {
    return "bg-red-50 text-red-700 ring-red-600/20";
  }
  return "bg-gray-50 text-gray-700 ring-gray-500/10";
};

export const BookingDetailBadge = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${className}`}
  >
    {children}
  </span>
);

export const CopyableText = ({
  value,
  displayValue,
  label = "Value",
  onCopied,
  className = "",
  mono = false,
}) => {
  const [copied, setCopied] = useState(false);
  const text = value == null || value === "" ? null : String(value);

  const handleCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      onCopied?.(label);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      onCopied?.("Unable to copy", "error");
    }
  };

  if (!text) {
    return <span className="text-gray-400">—</span>;
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={`Copy ${label}`}
      className={`group inline-flex max-w-full items-center gap-1.5 rounded-md text-left transition hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${className}`}
    >
      <span className={`truncate ${mono ? "font-mono text-xs" : ""}`}>
        {displayValue ?? text}
      </span>
      <i
        className={`shrink-0 text-[11px] ${
          copied ? "fi fi-rr-check text-emerald-600" : "fi fi-rr-copy text-gray-400 group-hover:text-primary"
        }`}
      />
      <span className="sr-only">Copy {label}</span>
    </button>
  );
};

export const BookingDetailNav = ({ items, activeId, onSelect }) => (
  <nav
    aria-label="Booking sections"
    className="sticky top-0 z-10 -mx-5 border-b border-gray-100 bg-white/95 px-5 py-2 backdrop-blur-sm"
  >
    <div className="flex gap-1 overflow-x-auto pb-0.5 scrollbar-hide">
      {items.map(({ id, label, icon }) => {
        const isActive = activeId === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onSelect(id)}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${
              isActive
                ? "bg-primary/10 text-primary"
                : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            }`}
          >
            {icon ? <i className={`${icon} text-[11px]`} /> : null}
            {label}
          </button>
        );
      })}
    </div>
  </nav>
);

export const BookingPaymentProgress = ({
  total,
  paid,
  balance,
  formatCurrency,
}) => {
  const totalNum = parseFloat(total) || 0;
  const paidNum = parseFloat(paid) || 0;
  const balanceNum = parseFloat(balance) || 0;
  const percent = totalNum > 0 ? Math.min(100, Math.round((paidNum / totalNum) * 100)) : 0;
  const isPaid = balanceNum <= 0 && totalNum > 0;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            Payment progress
          </p>
          <p className="mt-0.5 text-sm font-semibold text-gray-900">
            {formatCurrency(paidNum)} of {formatCurrency(totalNum)}
          </p>
        </div>
        <BookingDetailBadge
          className={
            isPaid
              ? statusBadgeClass("paid")
              : paidNum > 0
                ? statusBadgeClass("partially paid")
                : statusBadgeClass("unpaid")
          }
        >
          {isPaid ? "Fully paid" : paidNum > 0 ? `${percent}% paid` : "Unpaid"}
        </BookingDetailBadge>
      </div>
      <div
        className="h-2 overflow-hidden rounded-full bg-gray-100"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Payment progress"
      >
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            isPaid ? "bg-emerald-500" : "bg-primary"
          }`}
          style={{ width: `${percent}%` }}
        />
      </div>
      {balanceNum > 0 ? (
        <p className="mt-2 text-xs text-amber-700">
          <i className="fi fi-rr-info mr-1" />
          {formatCurrency(balanceNum)} remaining
        </p>
      ) : (
        <p className="mt-2 text-xs text-emerald-700">
          <i className="fi fi-rr-check mr-1" />
          No outstanding balance
        </p>
      )}
    </div>
  );
};

export const BookingDetailHeader = ({
  bookingId,
  idLabel = "Booking",
  reference,
  title,
  meta,
  badges,
  status,
  statusClassName,
  amount,
  bookedAt,
  bookedTime,
  bookedOnLabel = "Booked on",
  stats,
  onCopyBookingId,
}) => (
  <div className="overflow-hidden rounded-xl border border-gray-200 bg-gradient-to-br from-primary/[0.06] via-white to-white">
    <div className="border-b border-primary/10 px-4 py-4 sm:px-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary/80">
            <CopyableText
              value={bookingId}
              displayValue={`${idLabel} #${bookingId}`}
              label={`${idLabel} ID`}
              onCopied={onCopyBookingId}
              className="text-[11px] font-semibold uppercase tracking-wider text-primary/80"
            />
            {reference ? (
              <span className="ml-1.5 normal-case text-gray-500">· Ref {reference}</span>
            ) : null}
          </div>
          <h4 className="mt-1 text-lg font-semibold leading-snug text-gray-900 sm:text-xl">
            {title}
          </h4>
          {meta ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">{meta}</div>
          ) : null}
          {badges ? <div className="mt-2 flex flex-wrap gap-1.5">{badges}</div> : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
          {status ? (
            <BookingDetailBadge className={statusClassName || statusBadgeClass(status)}>
              {status}
            </BookingDetailBadge>
          ) : null}
          {amount ? (
            <BookingDetailBadge className="bg-primary/10 text-primary ring-primary/20">
              {amount}
            </BookingDetailBadge>
          ) : null}
        </div>
      </div>
      {bookedAt ? (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-gray-500">
          <i className="fi fi-rr-time-check text-[11px] text-gray-400" />
          {bookedOnLabel} {bookedAt}
          {bookedTime ? <span className="text-gray-400">· {bookedTime}</span> : null}
        </p>
      ) : null}
    </div>
    {stats?.length ? (
      <div className="grid grid-cols-2 divide-x divide-gray-100 border-t border-gray-100 bg-white/80 sm:grid-cols-4">
        {stats.map(({ label, value, icon, highlight }) => (
          <div key={label} className="px-4 py-3 text-center sm:px-3">
            {icon ? (
              <i className={`${icon} mb-1 block text-sm text-primary/70`} />
            ) : null}
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              {label}
            </p>
            <p
              className={`mt-0.5 text-sm font-semibold ${
                highlight ? "text-primary" : "text-gray-900"
              }`}
            >
              {value ?? "—"}
            </p>
          </div>
        ))}
      </div>
    ) : null}
  </div>
);

export const BookingDetailMetaChip = ({ icon, children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-600 shadow-sm">
    {icon ? <i className={`${icon} text-[10px] text-primary/70`} /> : null}
    {children}
  </span>
);

export const BookingDetailSection = ({
  id,
  title,
  icon,
  action,
  children,
  className = "",
}) => (
  <section
    id={id}
    className={`scroll-mt-20 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ${className}`}
  >
    <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/80 px-4 py-3">
      <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
        {icon ? <i className={`${icon} text-sm text-primary`} /> : null}
        {title}
      </h3>
      {action}
    </div>
    <div className="px-4 py-4">{children}</div>
  </section>
);

export const BookingDetailFields = ({ fields, columns = 2, onCopied }) => (
  <dl
    className={`grid gap-x-5 gap-y-4 ${
      columns === 1
        ? "grid-cols-1"
        : columns === 3
          ? "grid-cols-1 sm:grid-cols-3"
          : "grid-cols-1 sm:grid-cols-2"
    }`}
  >
    {fields.map(({ label, value, fullWidth, highlight, copyable, copyValue }, index) => (
      <div
        key={`${label}-${index}`}
        className={`rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2.5 ${
          fullWidth ? "sm:col-span-2" : ""
        }`}
      >
        <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          {label}
        </dt>
        <dd
          className={`mt-1 text-sm leading-snug ${
            highlight ? "font-semibold text-primary" : "font-medium text-gray-800"
          }`}
        >
          {copyable ? (
            <CopyableText
              value={copyValue ?? value}
              displayValue={value}
              label={label}
              onCopied={onCopied}
            />
          ) : (
            value ?? "—"
          )}
        </dd>
      </div>
    ))}
  </dl>
);

export const InfoRow = ({ label, value, icon, href, onCopied, copyValue }) => (
  <div className="flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2.5">
    {icon ? (
      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-primary shadow-sm">
        <i className={`${icon} text-xs`} />
      </span>
    ) : null}
    <div className="min-w-0 flex-1">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <div className="mt-0.5 break-all text-sm font-medium text-gray-800">
        {href && value ? (
          <a href={href} className="text-primary hover:underline">
            {value}
          </a>
        ) : copyValue != null && copyValue !== "" ? (
          <CopyableText value={copyValue} displayValue={value} label={label} onCopied={onCopied} />
        ) : (
          value ?? "—"
        )}
      </div>
    </div>
  </div>
);

export const BookingDetailUserCard = ({ user, onCopied, showMemberSince = true }) => {
  const initials = user?.name
    ? user.name
        .split(" ")
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "?";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="flex items-center gap-3 sm:w-48 sm:shrink-0">
          {user?.profile_image ? (
            <img
              src={user.profile_image}
              alt={user.name}
              className="h-14 w-14 rounded-full object-cover ring-2 ring-white shadow-md"
            />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-base font-semibold text-primary ring-2 ring-white shadow-md">
              {initials}
            </div>
          )}
          <div className="min-w-0 sm:hidden">
            <p className="truncate font-semibold text-gray-900">{user?.name || "—"}</p>
            <p className="truncate text-xs text-gray-500">{user?.email || "—"}</p>
          </div>
        </div>
        <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
          <InfoRow label="Name" value={user?.name} icon="fi fi-rr-user" />
          <InfoRow
            label="Email"
            value={user?.email}
            icon="fi fi-rr-envelope"
            href={user?.email ? `mailto:${user.email}` : undefined}
            copyValue={user?.email}
            onCopied={onCopied}
          />
          <InfoRow
            label="Phone"
            value={user?.phone}
            icon="fi fi-rr-phone-call"
            href={user?.phone ? `tel:${user.phone}` : undefined}
            copyValue={user?.phone}
            onCopied={onCopied}
          />
          {showMemberSince ? (
            <InfoRow
              label="Member Since"
              value={user?.created_at_formatted}
              icon="fi fi-rr-calendar"
            />
          ) : null}
          <div className="sm:col-span-2">
            <InfoRow
              label="Account Status"
              value={
                <BookingDetailBadge
                  className={
                    user?.is_blocked
                      ? statusBadgeClass("blocked")
                      : statusBadgeClass("active")
                  }
                >
                  {user?.is_blocked ? "Blocked" : "Active"}
                </BookingDetailBadge>
              }
              icon="fi fi-rr-shield-check"
            />
          </div>
        </div>
      </div>
      {(user?.email || user?.phone) && (
        <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-3">
          {user?.email ? (
            <a
              href={`mailto:${user.email}`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
            >
              <i className="fi fi-rr-envelope text-[11px]" />
              Send email
            </a>
          ) : null}
          {user?.phone ? (
            <a
              href={`tel:${user.phone}`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
            >
              <i className="fi fi-rr-phone-call text-[11px]" />
              Call customer
            </a>
          ) : null}
        </div>
      )}
    </div>
  );
};

export const BookingDetailEmptyState = ({ icon = "fi fi-rr-inbox", title, message }) => (
  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/50 px-6 py-10 text-center">
    <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-400">
      <i className={`${icon} text-base`} />
    </span>
    <p className="text-sm font-medium text-gray-700">{title}</p>
    {message ? <p className="mt-1 max-w-sm text-xs text-gray-500">{message}</p> : null}
  </div>
);

export const BookingDetailTable = ({
  columns,
  rows,
  emptyMessage = "No records found.",
  emptyTitle = "Nothing here yet",
}) => (
  <div className="overflow-hidden rounded-lg border border-gray-100">
    {rows.length > 0 ? (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-gray-50/80">
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  scope="col"
                  className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 bg-white">{rows}</tbody>
        </table>
      </div>
    ) : (
      <BookingDetailEmptyState title={emptyTitle} message={emptyMessage} />
    )}
  </div>
);
